/**
 * eventTemplates — Decision Reduction Engine (V1, rule-based).
 *
 * Purpose
 * -------
 * Translates a chosen `event_type` into a recommended set of services so the
 * customer arrives at Step 2 of Plan Event with a pre-populated checklist
 * rather than an empty grid. Reduces decision fatigue; the customer remains
 * in full control and can remove any essential or add any add-on.
 *
 * Contract
 * --------
 * `getTemplateForEventType(eventType: string)` → {
 *   essentials: string[],   // pre-selected on arrival to Step 2
 *   addOns:     string[],   // surfaced as optional below essentials
 *   fallbackUsed: boolean,  // true when no template matched the event type
 * }
 *
 * IMPORTANT:
 *   Every string in `essentials` and `addOns` MUST exactly match a backend
 *   vendor category (the same strings already used in
 *   `POPULAR_SERVICES.value` inside `pages/PlanEvent.jsx` and the
 *   `categories` field on backend vendor docs). The downstream payload
 *   shape (`selected_services: string[]`) is unchanged — this engine only
 *   decides the initial contents.
 *
 * Architecture
 * ------------
 * Pure rule layer. Keep this file free of network calls, AI calls, side
 * effects, and React imports. A future recommendation engine (vendor
 * supply-aware, AI-assisted, customer-history-aware) can replace this
 * function while preserving the signature.
 */

// Keys are the exact `event_type` values shipped in
// `constants/primaryEventTypes.js` (Step 1 tiles) plus a few common
// free-text variants users might enter via the "Not listed" search box.
// Lookup is case-insensitive on the consumer side.
export const EVENT_TEMPLATES = {
  'Birthday Party': {
    essentials: ['Venue', 'Cake Bakers', 'Photographer', 'Event Decor & Styling'],
    addOns: [
      'DJ',
      'Balloon Artist',
      'Bounce House / Inflatable Rentals',
      'Photo Booth Provider',
      'Face Painters',
      'Caterer',
      'Videographer',
    ],
  },
  'Wedding': {
    essentials: [
      'Venue',
      'Photographer',
      'Caterer',
      'Florist',
      'DJ',
      'Cake Bakers',
    ],
    addOns: [
      'Videographer',
      'Event Decor & Styling',
      'Hair & Makeup',
      'Event Planner',
      'Photo Booth Provider',
    ],
  },
  'Baby Shower': {
    essentials: [
      'Venue',
      'Event Decor & Styling',
      'Balloon Artist',
      'Cake Bakers',
      'Photographer',
    ],
    addOns: ['Caterer', 'Florist', 'Photo Booth Provider'],
  },
  'Graduation Party': {
    essentials: ['Venue', 'Photographer', 'Event Decor & Styling', 'Caterer'],
    addOns: [
      'DJ',
      'Balloon Artist',
      'Photo Booth Provider',
      'Videographer',
      'Cake Bakers',
    ],
  },
  'Corporate Event': {
    essentials: ['Venue', 'Caterer', 'Photographer'],
    addOns: ['DJ', 'Videographer', 'Event Decor & Styling', 'Event Planner'],
  },
  'Church Event': {
    essentials: ['Venue', 'Caterer', 'Photographer'],
    addOns: ['Videographer', 'DJ', 'Event Decor & Styling', 'Florist'],
  },
  'Anniversary': {
    essentials: ['Venue', 'Photographer', 'Caterer', 'Cake Bakers'],
    addOns: [
      'DJ',
      'Florist',
      'Event Decor & Styling',
      'Videographer',
      'Photo Booth Provider',
    ],
  },
  'Engagement Party': {
    essentials: ['Venue', 'Photographer', 'Caterer', 'Florist'],
    addOns: [
      'DJ',
      'Event Decor & Styling',
      'Cake Bakers',
      'Videographer',
      'Photo Booth Provider',
    ],
  },
  'Bridal Shower': {
    essentials: [
      'Venue',
      'Event Decor & Styling',
      'Florist',
      'Caterer',
      'Cake Bakers',
    ],
    addOns: ['Photographer', 'Balloon Artist', 'Photo Booth Provider'],
  },
  'Family Reunion': {
    essentials: ['Venue', 'Caterer', 'Photographer'],
    addOns: [
      'DJ',
      'Event Decor & Styling',
      'Bounce House / Inflatable Rentals',
      'Photo Booth Provider',
      'Face Painters',
    ],
  },
  'Private Party': {
    essentials: ['Venue', 'Caterer', 'DJ'],
    addOns: [
      'Photographer',
      'Event Decor & Styling',
      'Florist',
      'Cake Bakers',
      'Photo Booth Provider',
    ],
  },
};

// Fallback template for custom/free-text event types (anything not in the
// map above). Conservative and broadly applicable.
const FALLBACK_TEMPLATE = {
  essentials: ['Venue', 'Photographer', 'Caterer'],
  addOns: [
    'DJ',
    'Event Decor & Styling',
    'Florist',
    'Cake Bakers',
    'Photo Booth Provider',
    'Videographer',
    'Balloon Artist',
  ],
};

/**
 * Returns the suggested service template for a given event type. Always
 * returns a usable object — never null/undefined. Unknown event types
 * receive `FALLBACK_TEMPLATE` and `fallbackUsed === true` so the caller
 * can branch presentation if desired.
 */
export const getTemplateForEventType = (eventType) => {
  if (!eventType || typeof eventType !== 'string') {
    return { ...FALLBACK_TEMPLATE, fallbackUsed: true };
  }
  // Case-insensitive lookup for resilience against capitalization drift
  // from the free-text search path.
  const key = Object.keys(EVENT_TEMPLATES).find(
    (k) => k.toLowerCase() === eventType.trim().toLowerCase()
  );
  if (!key) {
    return { ...FALLBACK_TEMPLATE, fallbackUsed: true };
  }
  return { ...EVENT_TEMPLATES[key], fallbackUsed: false };
};
